import { Box, styled } from '@mui/material';
import React, { ReactNode } from 'react';

import { linkable } from '../../routing';
import { AppLinkOptions, asLinkProps } from '../../routing/links/linkOptions';
import { colorSystem, focusVisibleRing, INTER } from '../../theme';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';

interface CollapsedBreadcrumbs {
    type: 'collapsed-breadcrumb';
    title: string;
    breadcrumbs: BreadcrumbItem[];
}

export type BreadcrumbItem = {
    type?: 'regular-breadcrumb';
    title: JSX.Element | string;
} & (
    | {
          /** Build it with `linkOptions()`. A link crumb navigates by itself, so it takes no `onClick`. */
          link: AppLinkOptions;
          onClick?: never;
      }
    | {
          link?: never;
          /** A crumb without `link` is text; with `onClick` it is a button. */
          onClick?: () => void;
      }
);

interface ToolbarProps extends React.PropsWithChildren<{}> {
    breadcrumbs: Array<BreadcrumbItem>;
    left?: ReactNode;
    center?: ReactNode;
    style?: React.CSSProperties;
    hideLastBreadcrumb?: boolean;
}

const centerStyle: React.CSSProperties = { placeItems: 'center' };

/**
 * Tabs that a layout route gives the toolbar of each of its pages, such as a sourcing scenario's: the toolbar draws them
 * in a row below its own, so the page's title comes first. A page under that layout without a toolbar shows no tabs.
 */
export const ToolbarTabs = React.createContext<React.ReactNode>(null);

export function Toolbar({
    breadcrumbs,
    center,
    children: right,
    left,
    style,
    hideLastBreadcrumb = false,
}: ToolbarProps): JSX.Element {
    const collapsedBreadcrumbs = collapseBreadcrumbs(breadcrumbs, hideLastBreadcrumb);
    const tabs = React.useContext(ToolbarTabs);
    const bar = (
        <Container
            style={style}
            data-crumbs={collapsedBreadcrumbs.length > 0 ? '' : undefined}
            data-tabs={tabs === null ? undefined : ''}
        >
            <BreadcrumbsContainer>
                {collapsedBreadcrumbs.map((breadcrumb, i) => {
                    return (
                        <React.Fragment key={i}>
                            {i > 0 && (
                                <Text variant="h5" color={colorSystem.neutral[6]}>
                                    {'/'}
                                </Text>
                            )}
                            <Breadcrumb
                                key={i}
                                breadcrumb={breadcrumb}
                                isCurrent={i === collapsedBreadcrumbs.length - 1}
                            />
                        </React.Fragment>
                    );
                })}
                {left}
            </BreadcrumbsContainer>
            <Box
                style={centerStyle}
                sx={{
                    display: 'grid',
                }}
            >
                {center}
            </Box>
            <ActionsContainer>
                <React.Suspense fallback={<></>}>{right}</React.Suspense>
            </ActionsContainer>
        </Container>
    );
    return tabs === null ? (
        bar
    ) : (
        <TabsRow>
            {bar}
            {tabs}
        </TabsRow>
    );
}

/** The toolbar and the layout's tabs below it, which draw the line below both. */
const TabsRow = styled('div')({
    background: colorSystem.neutral.white,
});

const Container = styled('div')({
    display: 'grid',
    gridTemplateColumns: '1fr fit-content(32%) 1fr',
    padding: '8px 20px',
    // A crumb pads its text by 8 px: so it starts 20 px from the edge, as a page's title does.
    '&[data-crumbs]': { paddingLeft: '12px' },
    borderBottom: `1px solid ${colorSystem.neutral[2]}`,
    '&[data-tabs]': { borderBottom: 'none' },
    alignItems: 'center',
    background: colorSystem.neutral.white,
    boxSizing: 'border-box',
    minHeight: 49,
});

const BreadcrumbsContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 2,
});

const ActionsContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'flex-end',
});

function Breadcrumb({
    breadcrumb,
    isCurrent,
}: {
    breadcrumb: BreadcrumbItem | CollapsedBreadcrumbs;
    isCurrent: boolean;
}) {
    if (isCurrent) {
        return <StyledCurrentBreadcrumb variant={'h4'}>{breadcrumb.title}</StyledCurrentBreadcrumb>;
    }
    if (breadcrumb.type === 'collapsed-breadcrumb') {
        return (
            <Tooltip
                variant="white"
                arrow
                title={
                    <BreadcrumbsContainer>
                        {breadcrumb.breadcrumbs.map((breadcrumb, i) => {
                            return (
                                <React.Fragment key={i}>
                                    {i > 0 && (
                                        <Text variant="h5" color={colorSystem.neutral[6]}>
                                            {'/'}
                                        </Text>
                                    )}

                                    <SmallBreadcrumb breadcrumb={breadcrumb} />
                                </React.Fragment>
                            );
                        })}
                    </BreadcrumbsContainer>
                }
            >
                <StyledBreadcrumbTrigger type="button">
                    <Text variant="h5" color={colorSystem.neutral[6]}>
                        {breadcrumb.title}
                    </Text>
                </StyledBreadcrumbTrigger>
            </Tooltip>
        );
    }

    if (breadcrumb.link !== undefined) {
        return <StyledBreadcrumbAnchor {...asLinkProps(breadcrumb.link)}>{breadcrumb.title}</StyledBreadcrumbAnchor>;
    }
    return <StyledBreadcrumbText {...clickableTextProps(breadcrumb.onClick)}>{breadcrumb.title}</StyledBreadcrumbText>;
}

/** A crumb of the collapsed tooltip, in the small style. */
function SmallBreadcrumb({ breadcrumb }: { breadcrumb: BreadcrumbItem }) {
    const title = <Text color={'inherit'}>{breadcrumb.title}</Text>;
    if (breadcrumb.link !== undefined) {
        return <StyledSmallAnchor {...asLinkProps(breadcrumb.link)}>{title}</StyledSmallAnchor>;
    }
    return <StyledSmallText {...clickableTextProps(breadcrumb.onClick)}>{title}</StyledSmallText>;
}

function clickableTextProps(onClick: (() => void) | undefined): React.HTMLAttributes<HTMLElement> {
    if (onClick === undefined) {
        return {};
    }
    return {
        onClick,
        role: 'button',
        tabIndex: 0,
        style: { cursor: 'pointer' },
        onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
            }
        },
    };
}

const StyledCurrentBreadcrumb = styled(Text)({
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: 400,
    padding: '4px 8px',
    '&:hover': {
        color: colorSystem.neutral[9],
        backgroundColor: colorSystem.neutral[1],
    },
    borderRadius: 4,
});

const breadcrumbStyle = {
    textDecoration: 'none',
    color: colorSystem.neutral[6],
    '&:hover': {
        color: colorSystem.neutral[9],
        backgroundColor: colorSystem.neutral[1],
    },
    borderRadius: 4,
    padding: '4px 8px',
    fontFamily: INTER,
    minWidth: 0,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 600,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
} as const;

const StyledBreadcrumbAnchor = linkable(styled('a')(breadcrumbStyle, focusVisibleRing));
const StyledBreadcrumbText = styled('span')(breadcrumbStyle, focusVisibleRing);
/** The collapsed "..." crumb: a real button, so keyboard users can focus it to open the tooltip. */
const StyledBreadcrumbTrigger = styled('button')(
    {
        ...breadcrumbStyle,
        border: 'none',
        background: 'none',
        cursor: 'default',
    },
    focusVisibleRing,
);

const smallBreadcrumbStyle = {
    textDecoration: 'none',
    color: colorSystem.neutral[8],
    borderRadius: 4,
    padding: '4px 8px',
    '&:hover': {
        color: colorSystem.neutral[9],
        backgroundColor: colorSystem.neutral[1],
    },
    fontFamily: INTER,
    minWidth: 0,
    fontSize: 12,
    lineHeight: '20px',
} as const;

const StyledSmallAnchor = linkable(styled('a')(smallBreadcrumbStyle));
const StyledSmallText = styled('span')(smallBreadcrumbStyle);

/** Longer chains collapse the middle into "..." between the first and last two; four-step ones stay whole. */
const MAX_BREADCRUMBS_BEFORE_COLLAPSE = 4;

function collapseBreadcrumbs(
    breadcrumbs: BreadcrumbItem[],
    hideLastBreadcrumb: boolean,
): Array<BreadcrumbItem | CollapsedBreadcrumbs> {
    const breadcrumbsToShow = hideLastBreadcrumb ? breadcrumbs.slice(0, -1) : breadcrumbs;

    if (breadcrumbsToShow.length <= MAX_BREADCRUMBS_BEFORE_COLLAPSE) {
        return breadcrumbsToShow;
    }
    const first = breadcrumbsToShow[0];
    const lastTwo = breadcrumbsToShow.slice(-2);
    return [
        first,
        {
            type: 'collapsed-breadcrumb',
            title: '...',
            breadcrumbs: breadcrumbsToShow.slice(1, -2),
        },
        ...lastTwo,
    ];
}
